import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from "sonner";
import { FiCheck, FiX, FiRotateCcw, FiCalendar, FiChevronLeft, FiChevronRight, FiAward, FiMenu, FiClock, FiEdit2 } from 'react-icons/fi';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  getWeekStartDate, getWeekDays, updateTaskStatus, updateTask,
  getCoachNote, SUBJECT_COLORS, saveDayNoteWithWeek, getDayNotesForWeek,
  getAllTasksForStudent, getAllDayNotesForStudent
} from '../../utils/coachUtils';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import MiniCalendar from '../../components/MiniCalendar';
import '../../styles/StudentProgram.css';

// ── Droppable Day Tab ──
function DroppableDayTab({ day, isActive, isToday, allDone, dc, onSelect }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day-tab-${day.date}` });
  return (
    <button
      ref={setNodeRef}
      className={`sp-day-tab ${isActive ? 'sp-day-tab--active' : ''} ${isToday ? 'sp-day-tab--today' : ''} ${allDone ? 'sp-day-tab--done' : ''}`}
      style={{ border: isOver ? '2px dashed #4f46e5' : undefined }}
      onClick={() => onSelect(day.date)}
    >
      <span className="sp-day-tab-name">{day.dayName.slice(0, 3)}</span>
      <span className="sp-day-tab-number">{day.dayNumber}</span>
      {dc.length > 0 && (
        <span className="sp-day-tab-dots">
          {allDone ? <FiCheck size={10} /> : `${dc.filter(t => t.status === 'completed').length}/${dc.length}`}
        </span>
      )}
    </button>
  );
}

// ── Sortable Task Item ──
function SortableTaskItem({ task, celebrateTask, handleStatusChange, onViewTask }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id, data: { ...task } });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 99 : 1,
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`sp-task ${task.status !== 'pending' ? `sp-task--${task.status}` : ''} ${celebrateTask === task.id ? 'sp-task--celebrate' : ''}`}
      layout
    >
      {/* Drag Handle */}
      <div 
        {...listeners} 
        style={{ width: '20px', cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}
      >
        <FiMenu size={16} />
      </div>

      <div className="sp-task-stripe" style={{ background: SUBJECT_COLORS[task.subject] || '#64748b' }} />
      <div 
        className="sp-task-content" 
        onClick={() => onViewTask && onViewTask(task)}
        style={{ cursor: 'pointer' }}
      >
        <div className="sp-task-top">
          <span className="sp-task-title">
            {task.startTime && <span style={{ color: '#4f46e5', fontWeight: 800, marginRight: '6px' }}>{task.startTime}</span>}
            {task.title}
          </span>
          {task.status === 'completed' && <span className="sp-task-done-badge"><FiAward size={12} /> Tamamlandı</span>}
        </div>
        <div className="sp-task-meta">
          <span className="sp-task-tag" style={{ color: SUBJECT_COLORS[task.subject] || '#64748b' }}>
            {task.category} · {task.subject}
          </span>
          {task.duration && (
            <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 700, marginLeft: '8px' }}>⏱ {task.duration} dk</span>
          )}
        </div>
        {task.description && (
          <div className="sp-task-desc">
            {task.description.length > 40 ? task.description.slice(0, 40) + '...' : task.description}
          </div>
        )}
      </div>
      <div className="sp-task-actions">
        {task.status === 'pending' ? (
          <>
            <motion.button
              className="sp-action-btn sp-action-btn--done"
              onClick={() => handleStatusChange(task.id, 'completed')}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Tamamladım"
            >
              <FiCheck size={20} />
            </motion.button>
            <motion.button
              className="sp-action-btn sp-action-btn--skip"
              onClick={() => handleStatusChange(task.id, 'skipped')}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Atlıyorum"
            >
              <FiX size={18} />
            </motion.button>
          </>
        ) : (
          <motion.button
            className="sp-action-btn sp-action-btn--undo"
            onClick={() => handleStatusChange(task.id, 'pending')}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Geri Al"
          >
            <FiRotateCcw size={16} />
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

export default function StudentProgramPage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');

  const [coachInfo, setCoachInfo] = useState(null);
  const [weekStart, setWeekStart] = useState(getWeekStartDate());
  const [tasks, setTasks] = useState([]);
  const [coachNote, setCoachNote] = useState(null);
  const [selectedDay, setSelectedDay] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  
  // Day Notes state
  const [weekNotes, setWeekNotes] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [dayNoteText, setDayNoteText] = useState('');
  const [hasCoach, setHasCoach] = useState(false);
  const [celebrateTask, setCelebrateTask] = useState(null);
  const [viewTask, setViewTask] = useState(null);

  // All student data for MiniCalendar
  const [allStudentTasks, setAllStudentTasks] = useState([]);
  const [allStudentNotes, setAllStudentNotes] = useState([]);

  const fetchData = useCallback(async () => {
    if (!userId) {
      setHasCoach(false);
      setLoading(false);
      return;
    }
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) return;

      const userData = userDoc.data();
      const coachId = userData.coachId;

      if (!coachId) { setHasCoach(false); setLoading(false); return; }
      setHasCoach(true);

      // Coach info
      const coachRef = doc(db, 'users', coachId);
      const coachDoc = await getDoc(coachRef);
      if (coachDoc.exists()) setCoachInfo({ id: coachDoc.id, ...coachDoc.data() });

      // Tasks
      const q = query(
        collection(db, 'coachTasks'),
        where('studentId', '==', userId),
        where('weekStartDate', '==', weekStart)
      );
      const snapshot = await getDocs(q);
      const t = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      t.sort((a, b) => a.date.localeCompare(b.date) || (a.order || 0) - (b.order || 0));
      setTasks(t);

      // Coach note
      const noteData = await getCoachNote(coachId, userId);
      setCoachNote(noteData);

      const notes = await getDayNotesForWeek(userId, weekStart);
      setWeekNotes(notes);
    } catch (err) {
      console.error('Error fetching student program:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, weekStart]);

  const fetchAllStudentData = useCallback(async () => {
    if (!userId) return;
    try {
      const t = await getAllTasksForStudent(userId);
      const n = await getAllDayNotesForStudent(userId);
      setAllStudentTasks(t);
      setAllStudentNotes(n);
    } catch (err) {
      console.error('Error fetching all data:', err);
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchAllStudentData(); }, [fetchAllStudentData]);

  const weekDays = getWeekDays(weekStart);
  const today = new Date().toISOString().split('T')[0];
  const dayTasks = tasks.filter(t => t.date === selectedDay);

  // Stats
  const totalWeekTasks = tasks.length;
  const completedWeekTasks = tasks.filter(t => t.status === 'completed').length;
  const pendingWeekTasks = tasks.filter(t => t.status === 'pending').length;
  const skippedWeekTasks = tasks.filter(t => t.status === 'skipped').length;
  const progressPercent = totalWeekTasks > 0 ? Math.round((completedWeekTasks / totalWeekTasks) * 100) : 0;
  const totalWeekDuration = tasks.reduce((acc, t) => acc + (Number(t.duration) || 0), 0);

  // Streak calculation (consecutive completed days this week)
  const streakCount = (() => {
    let streak = 0;
    for (const day of weekDays) {
      const dt = tasks.filter(t => t.date === day.date);
      if (dt.length === 0) continue;
      if (dt.every(t => t.status === 'completed')) streak++;
      else break;
    }
    return streak;
  })();

  // Week label
  const weekEndDate = new Date(weekStart);
  weekEndDate.setDate(weekEndDate.getDate() + 6);
  const formatDate = (d) => {
    const date = new Date(d);
    const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
    return `${date.getDate()} ${months[date.getMonth()]}`;
  };
  const weekLabel = `${formatDate(weekStart)} – ${formatDate(weekEndDate)}`;

  const navigateWeek = (direction) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + direction * 7);
    setWeekStart(d.toISOString().split('T')[0]);
  };

  const handleSaveDayNote = async () => {
    try {
      await saveDayNoteWithWeek(userId, selectedDay, weekStart, dayNoteText);
      toast.success('Not kaydedildi ✓');
      setShowNoteModal(false);
      fetchData(); // Refresh notes
    } catch (err) {
      toast.error('Not kaydedilemedi');
    }
  };

  const handleStatusChange = async (taskId, status) => {
    try {
      await updateTaskStatus(taskId, status);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status, completedAt: status === 'completed' ? new Date() : null } : t));

      if (status === 'completed') {
        setCelebrateTask(taskId);
        setTimeout(() => setCelebrateTask(null), 1200);
        toast.success('Harika! Görev tamamlandı 🎉');
      } else if (status === 'skipped') {
        toast('Görev atlandı', { type: 'info' });
      } else {
        toast('Durum sıfırlandı', { type: 'info' });
      }
    } catch { toast.error('Güncellenemedi'); }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [activeId, setActiveId] = useState(null);

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = async (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const activeTask = tasks.find(t => t.id === activeId);
    if (!activeTask) return;

    let targetDate = activeTask.date;
    let newOrder = activeTask.order;

    const overDayCol = String(overId).startsWith('day-tab-') ? String(overId).replace('day-tab-', '') : null;
    
    if (overDayCol) {
      targetDate = overDayCol;
      const tasksInTargetDay = tasks.filter(t => t.date === targetDate);
      newOrder = tasksInTargetDay.length;
    } else {
      const overTask = tasks.find(t => t.id === overId);
      if (overTask) {
        targetDate = overTask.date;
        newOrder = overTask.order;
      }
    }

    if (activeTask.date === targetDate && activeTask.id === overId) return;

    // Optimistic Update
    setTasks(prev => prev.map(t => {
      if (t.id === activeId) return { ...t, date: targetDate, order: newOrder };
      return t;
    }));

    try {
      await updateTask(activeId, { date: targetDate, order: newOrder });
    } catch (err) {
      toast.error('Sıralama güncellenemedi');
      fetchData(); // Rollback
    }
  };

  if (loading) {
    return (
      <div className="sp-page">
        <div className="sp-loading"><div className="sp-loading-spinner" /><span>Yükleniyor...</span></div>
      </div>
    );
  }

  if (!hasCoach) {
    return (
      <div style={{
        backgroundColor: '#05050f', /* Deep cosmic navy/black */
        minHeight: '100vh',
        color: '#f8fafc',
        fontFamily: "'Inter', sans-serif",
        overflowX: 'hidden',
        position: 'relative',
        paddingBottom: '4rem'
      }}>
        {/* Background Glows */}
        <div style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, rgba(0,0,0,0) 70%)',
          filter: 'blur(60px)',
          zIndex: 0,
        }} />
        <div style={{
          position: 'absolute',
          top: '30%',
          right: '-5%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(168,85,247,0.1) 0%, rgba(0,0,0,0) 70%)',
          filter: 'blur(80px)',
          zIndex: 0,
        }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '1100px', margin: '0 auto', padding: '0 1.5rem' }}>
          
          {/* --- 1. HERO SECTION --- */}
          <section style={{ textAlign: 'center', paddingTop: '6rem', paddingBottom: '4rem' }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <h1 style={{
                fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                fontWeight: '900',
                letterSpacing: '-1.5px',
                lineHeight: '1.1',
                marginBottom: '1.5rem',
                color: '#fff',
                textShadow: '0 0 40px rgba(168,85,247,0.5), 0 0 20px rgba(99,102,241,0.4)',
              }}>
                Masada Tek Başına <br/>
                <span style={{
                  background: 'linear-gradient(135deg, #a855f7, #6366f1, #3b82f6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>Kriz Geçirme Devri Bitti.</span>
              </h1>
              <p style={{
                fontSize: '1.125rem',
                color: '#94a3b8',
                maxWidth: '700px',
                margin: '0 auto 2.5rem',
                lineHeight: '1.6',
              }}>
                Türkiye'nin en iyi derece öğrencileriyle çalış, hedefini şansa bırakma. Sana özel günlük program, taktikler ve haftalık takip tek bir panelde.
              </p>

              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(168,85,247,0.8)' }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://eksen.studio/', '_blank')}
                style={{
                  background: 'linear-gradient(135deg, #7e22ce, #4f46e5)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  padding: '1.2rem 2.5rem',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  boxShadow: '0 10px 25px -5px rgba(126, 34, 206, 0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Koçluk Paketlerini İncele 🚀
              </motion.button>
            </motion.div>
          </section>

          {/* --- 2. SNEAK PEEK (Interactive Dashboard Preview) --- */}
          <section style={{ margin: '4rem 0', position: 'relative' }}>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                borderRadius: '24px',
                padding: '2rem',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.6)'
              }}
            >
              {/* Fake Dashboard Content */}
              <div style={{ filter: 'blur(5px)', opacity: 0.6, pointerEvents: 'none', userSelect: 'none' }}>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#334155' }}></div>
                  <div>
                    <div style={{ width: '120px', height: '12px', background: '#334155', borderRadius: '4px', marginBottom: '8px' }}></div>
                    <div style={{ width: '80px', height: '10px', background: '#1e293b', borderRadius: '4px' }}></div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', overflowX: 'hidden' }}>
                  {[1, 2, 3].map(i => (
                    <div key={i} style={{ flex: 1, background: 'rgba(255,255,255,0.02)', borderRadius: '12px', padding: '1rem', border: '1px solid rgba(255,255,255,0.03)' }}>
                      <div style={{ width: '40px', height: '12px', background: '#334155', borderRadius: '4px', marginBottom: '1.5rem' }}></div>
                      
                      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', borderLeft: '3px solid #6366f1' }}>
                        <div style={{ fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '6px' }}>AYT Fizik - Sınav Taktikleri (Youtube)</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Fizik • 45 dk</div>
                      </div>

                      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '1rem', borderLeft: '3px solid #f43f5e' }}>
                        <div style={{ fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '6px' }}>Termodinamik - Hoca Ne Sorar?</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Kimya • 60 dk</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Glassmorphism Overlay */}
              <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(15, 23, 42, 0.4)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '2rem'
              }}>
                <div style={{
                  width: '64px', height: '64px',
                  background: 'rgba(99, 102, 241, 0.2)',
                  border: '1px solid rgba(99, 102, 241, 0.5)',
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '1rem',
                  boxShadow: '0 0 20px rgba(99,102,241,0.4)'
                }}>
                  <svg width="28" height="32" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                </div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#fff', marginBottom: '0.5rem', letterSpacing: '-0.5px' }}>
                  Böyle Bir Programla Sınavları Patlatmak İster miydin?
                </h3>
                <p style={{ color: '#cbd5e1', fontSize: '1rem' }}>Kontenjanlar dolmadan yerini ayırt.</p>
              </div>
            </motion.div>
          </section>

          {/* --- 3. HOW IT WORKS --- */}
          <section style={{ margin: '6rem 0' }}>
            <h2 style={{ textAlign: 'center', fontSize: '2rem', fontWeight: '800', marginBottom: '3rem', color: '#fff' }}>
              Sistem Nasıl İşliyor?
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem'
            }}>
              {[
                { 
                  step: '01', title: "Eksen'den Paketini Seç", 
                  desc: "eksen.studio adresinden hedefine uygun koçluk paketini saniyeler içinde al." 
                },
                { 
                  step: '02', title: "Derece Öğrencinle Eşleş", 
                  desc: "Kendi okulundan, kendi bölümünden o yollardan geçmiş onaylı koçunla eşleş." 
                },
                { 
                  step: '03', title: "Sistemi Çöz, Hedefi Yakala", 
                  desc: "Koçun her gün görevlerini yüklesin, sen tikle ve başarıyı garantile." 
                }
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: '20px',
                    padding: '2rem',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    fontSize: '4rem', fontWeight: '900', color: 'rgba(255,255,255,0.03)',
                    position: 'absolute', top: '-10px', right: '10px', lineHeight: '1'
                  }}>{item.step}</div>
                  
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px',
                    background: 'rgba(99,102,241,0.1)', color: '#818cf8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.2rem', fontWeight: '800', marginBottom: '1.5rem',
                    border: '1px solid rgba(99,102,241,0.2)'
                  }}>{item.step}</div>
                  
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#f8fafc', marginBottom: '1rem' }}>
                    {item.title}
                  </h3>
                  <p style={{ color: '#94a3b8', lineHeight: '1.6', fontSize: '0.95rem' }}>
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* --- 4. VALUE PROPOSITIONS --- */}
          <section style={{ margin: '6rem 0' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem'
            }}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                style={{
                  background: 'linear-gradient(135deg, rgba(15,23,42,0.8), rgba(30,41,59,0.8))',
                  borderLeft: '4px solid #a855f7',
                  padding: '2.5rem',
                  borderRadius: '0 20px 20px 0',
                }}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>💸</div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#fff', marginBottom: '1rem' }}>
                  Piyasanın 3'te 1 Fiyatına
                </h3>
                <p style={{ color: '#cbd5e1', lineHeight: '1.6' }}>
                  Elite akademik destek artık lüks değil, herkes için erişilebilir. Binlerce lira ödemeden en iyi taktikleri al.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                style={{
                  background: 'linear-gradient(135deg, rgba(15,23,42,0.8), rgba(30,41,59,0.8))',
                  borderLeft: '4px solid #3b82f6',
                  padding: '2.5rem',
                  borderRadius: '0 20px 20px 0',
                }}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🧠</div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#fff', marginBottom: '1rem' }}>
                  Hocadan Değil Akranından
                </h3>
                <p style={{ color: '#cbd5e1', lineHeight: '1.6' }}>
                  Kitabi teorilerle vakit kaybetme, o dersi AA ile geçmiş birinden gerçek sınav taktiklerini ve kısa yolları öğren.
                </p>
              </motion.div>
            </div>
          </section>

          {/* --- 5. FINAL FOOTER CTA --- */}
          <section style={{ margin: '4rem 0' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              style={{
                background: 'rgba(15, 23, 42, 0.4)',
                border: '1px solid rgba(168, 85, 247, 0.4)',
                borderRadius: '24px',
                padding: '4rem 2rem',
                textAlign: 'center',
                boxShadow: '0 0 40px rgba(168, 85, 247, 0.15), inset 0 0 20px rgba(99, 102, 241, 0.1)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{
                position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                width: '100%', height: '2px', background: 'linear-gradient(90deg, transparent, #a855f7, #6366f1, transparent)'
              }}></div>

              <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#fff', marginBottom: '1.5rem', letterSpacing: '-0.5px' }}>
                Yeni Dönemi ve Bütleri Şansa Bırakma.
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '1.1rem', marginBottom: '2.5rem' }}>
                Şimdi Eksen Studio güvencesiyle koçluk sistemine dahil ol.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open('https://eksen.studio', '_blank')}
                  style={{
                    background: '#fff',
                    color: '#0f172a',
                    border: 'none',
                    padding: '1rem 3rem',
                    fontSize: '1.1rem',
                    fontWeight: '800',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    boxShadow: '0 10px 20px rgba(255,255,255,0.2)'
                  }}
                >
                  eksen.studio'yu Ziyaret Et
                </motion.button>
                
                <a 
                  href="https://wa.me/" 
                  target="_blank" 
                  rel="noreferrer"
                  style={{ 
                    color: '#818cf8', 
                    textDecoration: 'none', 
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
                  </svg>
                  Kafana takılanlar mı var? WhatsApp'tan Yaz
                </a>
              </div>
            </motion.div>
          </section>

        </div>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="sp-page">
        {/* Hero */}
        <motion.div className="sp-hero" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="sp-hero-left">
            <h1 className="sp-hero-title">Haftalık <span>Programım</span></h1>
            {coachInfo && (
              <div className="sp-hero-coach">
                <div className="sp-hero-coach-avatar">{(coachInfo.username || '?')[0].toUpperCase()}</div>
                <span>Koç: <strong>{coachInfo.username}</strong></span>
              </div>
            )}
          </div>
          {streakCount > 0 && (
            <motion.div className="sp-streak" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.3 }}>
              <span className="sp-streak-fire">🔥</span>
              <span className="sp-streak-count">{streakCount}</span>
              <span className="sp-streak-label">gün seri</span>
            </motion.div>
          )}
        </motion.div>

        {/* ── Stat Cards ── */}
        <motion.div className="sp-stat-cards" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div className="sp-stat-card">
            <div className="sp-stat-icon sp-stat-icon--done"><FiCheck size={20} /></div>
            <div className="sp-stat-value">{completedWeekTasks}</div>
            <div className="sp-stat-label">Tamamlanan</div>
          </div>
          <div className="sp-stat-card">
            <div className="sp-stat-icon sp-stat-icon--pending"><FiCalendar size={20} /></div>
            <div className="sp-stat-value">{pendingWeekTasks}</div>
            <div className="sp-stat-label">Bekleyen</div>
          </div>
          <div className="sp-stat-card">
            <div className="sp-stat-icon sp-stat-icon--skipped"><FiX size={20} /></div>
            <div className="sp-stat-value">{skippedWeekTasks}</div>
            <div className="sp-stat-label">Atlanan</div>
          </div>
          <div className="sp-stat-card">
            <div className="sp-stat-icon sp-stat-icon--rate">
              <div className="sp-stat-ring">
                <svg viewBox="0 0 120 120" className="sp-stat-ring-svg">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#10b981" strokeWidth="10" strokeLinecap="round"
                    strokeDasharray={`${progressPercent * 3.14} 314`}
                    style={{ transition: 'stroke-dasharray 0.6s ease' }}
                  />
                </svg>
                <div className="sp-stat-ring-text">{progressPercent}%</div>
              </div>
            </div>
            <div className="sp-stat-label">Haftalık Oran</div>
          </div>
        </motion.div>

        {/* ── Coaching Data Entry Widgets ── */}
        <motion.div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
          <button 
            onClick={() => navigate('/trial')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '16px', background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 0.05))', border: '1px solid rgba(33, 150, 243, 0.2)', borderRadius: '16px', color: '#1e293b', cursor: 'pointer', transition: 'all 0.2s' }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <span style={{ fontSize: '24px' }}>📊</span>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>Sınav Sonucunu Gir</span>
            <span style={{ fontSize: '11px', color: '#64748b' }}>Koçunla senkronize ol</span>
          </button>
          
          <button 
            onClick={() => navigate('/study-time')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '16px', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05))', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '16px', color: '#1e293b', cursor: 'pointer', transition: 'all 0.2s' }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <span style={{ fontSize: '24px' }}>⏱️</span>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>Çalışma Başlat</span>
            <span style={{ fontSize: '11px', color: '#64748b' }}>Süreyi koçun görsün</span>
          </button>
        </motion.div>

        {/* ── Mini Calendar ── */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} style={{ marginBottom: '24px' }}>
          <MiniCalendar 
            tasks={allStudentTasks}
            dayNotes={allStudentNotes}
            selectedDate={selectedDay}
            onDayClick={(date) => {
              setWeekStart(getWeekStartDate(new Date(date)));
              setSelectedDay(date);
            }}
          />
        </motion.div>

        {/* ── Week Navigator ── */}
        {totalWeekTasks > 0 && (
          <motion.div className="sp-progress" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <div className="sp-progress-ring-wrap">
              <svg viewBox="0 0 120 120" className="sp-progress-ring">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#f1f5f9" strokeWidth="8" />
                <circle cx="60" cy="60" r="52" fill="none" stroke="url(#progressGrad)" strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${progressPercent * 3.27} 327`}
                  transform="rotate(-90 60 60)"
                  style={{ transition: 'stroke-dasharray 0.6s ease' }}
                />
                <defs>
                  <linearGradient id="progressGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="sp-progress-ring-text">
                <span className="sp-progress-ring-value">{progressPercent}</span>
                <span className="sp-progress-ring-unit">%</span>
              </div>
            </div>
            <div className="sp-progress-info">
              <div className="sp-progress-title">Haftalık İlerleme</div>
              <div className="sp-progress-bar-wrap">
                <div className="sp-progress-bar">
                  <div className="sp-progress-fill" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
              <div className="sp-progress-details">
                <span className="sp-progress-detail sp-progress-detail--done"><FiCheck size={12} /> {completedWeekTasks} tamamlandı</span>
                <span className="sp-progress-detail sp-progress-detail--left">◯ {totalWeekTasks - completedWeekTasks} kalan</span>
                {totalWeekDuration > 0 && (
                  <span className="sp-progress-detail" style={{ color: '#10b981' }}>
                    ⏱ {Math.floor(totalWeekDuration / 60) > 0 ? `${Math.floor(totalWeekDuration / 60)} sa ` : ''}{totalWeekDuration % 60 > 0 ? `${totalWeekDuration % 60} dk` : ''} toplam
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Week Navigation */}
        <div className="sp-week-nav">
          <button className="sp-week-btn" onClick={() => navigateWeek(-1)}><FiChevronLeft size={18} /></button>
          <div className="sp-week-center">
            <FiCalendar size={14} />
            <span>{weekLabel}</span>
          </div>
          <button className="sp-week-btn" onClick={() => navigateWeek(1)}><FiChevronRight size={18} /></button>
          <button className="sp-today-btn" onClick={() => { setWeekStart(getWeekStartDate()); setSelectedDay(today); }}>
            Bugün
          </button>
        </div>

        {/* Day Tabs */}
        <div className="sp-day-tabs">
          {weekDays.map(day => {
            const isActive = day.date === selectedDay;
            const isToday = day.date === today;
            const dc = tasks.filter(t => t.date === day.date);
            const done = dc.filter(t => t.status === 'completed').length;
            const allDone = dc.length > 0 && done === dc.length;
            const dayDur = dc.reduce((a, t) => a + (Number(t.duration) || 0), 0);
            return (
              <DroppableDayTab
                key={day.date}
                day={day}
                isActive={isActive}
                isToday={isToday}
                allDone={allDone}
                dc={dc}
                onSelect={setSelectedDay}
              />
            );
          })}
        </div>

        {/* ── Selected Day Header ── */}
        {(() => {
          const selectedDateObj = weekDays.find(d => d.date === selectedDay);
          const dayTotalDuration = dayTasks.reduce((acc, t) => acc + (Number(t.duration) || 0), 0);
          const hours = Math.floor(dayTotalDuration / 60);
          const mins = dayTotalDuration % 60;
          const durationStr = hours > 0 ? `${hours} sa ${mins > 0 ? mins + ' dk' : ''}` : `${mins} dk`;
          const dayCompleted = dayTasks.filter(t => t.status === 'completed').length;
          return (
            <motion.div className="sp-day-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} key={selectedDay}>
              <div className="sp-day-header-left">
                <span className="sp-day-header-date">{selectedDateObj ? selectedDateObj.dayNumber : ''}</span>
                <span className="sp-day-header-name">{selectedDateObj ? selectedDateObj.dayName : 'Program'}</span>
              </div>
              <div className="sp-day-header-right">
                <span className="sp-day-header-badge sp-day-header-badge--count">
                  {dayCompleted}/{dayTasks.length} Görev
                </span>
                {dayTotalDuration > 0 && (
                  <span className="sp-day-header-badge sp-day-header-badge--duration">
                    ⏱ {durationStr}
                  </span>
                )}
              </div>
              
              {/* Day Note Display */}
              {(() => {
                const noteObj = weekNotes.find(n => n.date === selectedDay);
                return (
                  <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Günün Anlamı / Notu</span>
                      <button 
                        onClick={() => {
                          setDayNoteText(noteObj ? noteObj.note : '');
                          setShowNoteModal(true);
                        }}
                        style={{ background: 'transparent', border: 'none', color: noteObj?.note ? '#4f46e5' : '#94a3b8', fontSize: '11px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        <FiEdit2 size={12} /> {noteObj?.note ? 'Düzenle' : 'Ekle'}
                      </button>
                    </div>
                    {noteObj?.note && (
                      <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', color: '#1e293b', fontSize: '13px', fontStyle: 'italic' }}>
                        📌 {noteObj.note}
                      </div>
                    )}
                  </div>
                );
              })()}
            </motion.div>
          );
        })()}

        {/* Tasks */}
        <div className="sp-task-list">
          <SortableContext items={dayTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
            <AnimatePresence mode="wait">
              {dayTasks.length === 0 ? (
                <motion.div key="empty" className="sp-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <FiCalendar size={32} />
                  <p>Bu gün için görev yok</p>
                </motion.div>
              ) : (
                dayTasks.map((task) => (
                  <SortableTaskItem 
                    key={task.id}
                    task={task}
                    celebrateTask={celebrateTask}
                    handleStatusChange={handleStatusChange}
                    onViewTask={setViewTask}
                  />
                ))
              )}
            </AnimatePresence>
          </SortableContext>
        </div>

        {/* Coach Notes */}
        {coachNote && coachNote.content && (
          <motion.div className="sp-coach-notes" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <div className="sp-coach-notes-header">
              <span>📝</span>
              <span>Koçunuzun Notları</span>
            </div>
            <div className="sp-coach-notes-body">{coachNote.content}</div>
          </motion.div>
        )}
      {/* Drag Overlay */}
      <DragOverlay>
        {activeId ? (
          <SortableTaskItem 
            task={tasks.find(t => t.id === activeId)} 
            celebrateTask={celebrateTask}
            handleStatusChange={() => {}}
          />
        ) : null}
      </DragOverlay>

      {/* Task Detail Modal */}
      <AnimatePresence>
        {viewTask && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            onClick={() => setViewTask(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', 
              zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
              backdropFilter: 'blur(5px)'
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '24px', padding: '24px', width: '100%', maxWidth: '400px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.5)', position: 'relative'
              }}
            >
              <button 
                onClick={() => setViewTask(null)}
                style={{ position: 'absolute', top: '16px', right: '16px', background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '20px' }}
              >✕</button>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: SUBJECT_COLORS[viewTask.subject] || '#64748b' }} />
                <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 600 }}>{viewTask.category} • {viewTask.subject}</span>
              </div>
              
              <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#fff', marginBottom: '16px', lineHeight: 1.3 }}>
                {viewTask.title}
              </h3>
              
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                {viewTask.startTime && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(79,70,229,0.1)', padding: '6px 12px', borderRadius: '8px', color: '#818cf8', fontSize: '13px', fontWeight: 700 }}>
                    <FiClock size={14} /> {viewTask.startTime}
                  </div>
                )}
                {viewTask.duration && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(16,185,129,0.1)', padding: '6px 12px', borderRadius: '8px', color: '#10b981', fontSize: '13px', fontWeight: 700 }}>
                    ⏱ {viewTask.duration} dk
                  </div>
                )}
              </div>
              
              {viewTask.description ? (
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700 }}>Açıklama</div>
                  <div style={{ fontSize: '14px', color: '#e2e8f0', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {viewTask.description}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '14px', color: '#64748b', fontStyle: 'italic', textAlign: 'center', padding: '10px 0' }}>
                  Açıklama eklenmemiş.
                </div>
              )}
              
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </DndContext>
  );
}
